import { assertCompleteRows } from "./aggregateState";
// ==========================================================================
// lib/shares.ts — shares: holdings, capital gains, distributions, prices
//   (FF-048, FF-049; 10 Oct 2026)
//   Capital gains, worked out from the trades:
//   - parcels are sold first in, first out, per owner and code; brokerage on
//     a buy adds to its cost, brokerage on a sell comes off the proceeds
//   - the 50% discount applies when the shares were held more than 12 months
//     (sold after the anniversary of the buy), for a person or a trust; not a company
//   - each year's losses (and losses carried forward) come off gains without
//     the discount first, then the discount gains, then the discount applies;
//     what's left of the losses carries forward
//   ETF cost base adjustments (from each fund's annual tax statement) aren't
//   in yet. A starting point for the accountant, not tax advice.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addYears, parseISO } from "date-fns";
import { supabase } from "./supabase";
import { useHouseholdId, type Entity } from "./household";
import { fyOf } from "./vehicles";
import { worker } from "./receipts";

export type Security = {
  id: string; exchange: string; code: string; name: string | null; kind: "share" | "etf" | "other";
  last_price: number | null; last_price_on: string | null; price_source_url: string | null; price_checked_at: string | null; archived_at: string | null;
};
export type Trade = {
  id: string; security_id: string; owner_entity_id: string; account_id: string | null; side: "buy" | "sell"; trade_date: string; settle_date: string | null;
  units: number; price: number; brokerage_cents: number; fees_cents: number; reference: string | null; note: string | null;
  cost_unknown?: boolean;   // an opening holding from before our records (021): units known, cost not
};
export type Income = { id: string; security_id: string; owner_entity_id: string; account_id: string | null; paid_on: string; amount_cents: number; franking_credit_cents: number; note: string | null };
export type PriceCheck = { id: string; security_id: string; price: number | null; as_at: string | null; source_url: string | null; source_title: string | null; cited_text: string | null; outcome: "used" | "held_back" | "not_found" | "failed"; message: string | null; created_at: string };

const num = (v: unknown) => (v === null || v === undefined ? null : Number(v));

export function useShares() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["shares", hid], enabled: !!hid,
    queryFn: async () => {
      const [s, t, i, c] = await Promise.all([
        supabase.from("securities").select("id,exchange,code,name,kind,last_price,last_price_on,price_source_url,price_checked_at,archived_at", { count: "exact" }).eq("household_id", hid).order("code"),
        supabase.from("share_trades").select("id,security_id,owner_entity_id,account_id,side,trade_date,settle_date,units,price,brokerage_cents,fees_cents,reference,note,cost_unknown", { count: "exact" }).eq("household_id", hid).is("deleted_at", null).order("trade_date").order("created_at").limit(5000),
        supabase.from("share_income").select("id,security_id,owner_entity_id,account_id,paid_on,amount_cents,franking_credit_cents,note", { count: "exact" }).eq("household_id", hid).is("deleted_at", null).order("paid_on", { ascending: false }).limit(5000),
        supabase.from("price_checks").select("id,security_id,price,as_at,source_url,source_title,cited_text,outcome,message,created_at").eq("household_id", hid).order("created_at", { ascending: false }).limit(200),
      ]);
      for (const r of [s, t, i, c]) if (r.error) throw r.error;
      for (const r of [s, t, i]) assertCompleteRows(r, "Investments");
      return {
        securities: (s.data as Security[]).map((x) => ({ ...x, last_price: num(x.last_price) })),
        trades: (t.data as Trade[]).map((x) => ({ ...x, units: Number(x.units), price: Number(x.price), brokerage_cents: Number(x.brokerage_cents), fees_cents: Number(x.fees_cents) })),
        income: (i.data as Income[]).map((x) => ({ ...x, amount_cents: Number(x.amount_cents), franking_credit_cents: Number(x.franking_credit_cents) })),
        checks: (c.data as PriceCheck[]).map((x) => ({ ...x, price: num(x.price) })),
      };
    },
  });
}

// ---------------------------------------------------------------------------
// The sums
// ---------------------------------------------------------------------------
export type Parcel = { tradeId: string; owner: string; security: string; account: string | null; date: string; units: number; costCents: number; costUnknown: boolean };
export type Disposal = {
  sellId: string; owner: string; security: string; date: string; fy: string; boughtOn: string;
  units: number; proceedsCents: number; costCents: number; gainCents: number; discountable: boolean; costUnknown: boolean;
};
export type Holding = { owner: string; security: string; account: string | null; units: number; costCents: number; firstBought: string; parcels: Parcel[]; unknownUnits: number };

/** Run the trades in date order: what's still held, and every sale matched to its parcels (first in, first out). */
export function workOut(trades: Trade[]) {
  const open = new Map<string, Parcel[]>();      // owner|security → parcels still held
  const disposals: Disposal[] = []; const problems: { security: string; owner: string; date: string; units: number }[] = [];
  const sorted = [...trades].sort((a, b) => a.trade_date.localeCompare(b.trade_date) || (a.side === "buy" ? -1 : 1));
  for (const t of sorted) {
    const key = `${t.owner_entity_id}|${t.security_id}`;
    const list = open.get(key) ?? [];
    if (t.side === "buy") {
      list.push({ tradeId: t.id, owner: t.owner_entity_id, security: t.security_id, account: t.account_id, date: t.trade_date, units: t.units, costCents: t.cost_unknown ? 0 : Math.round(t.units * t.price * 100) + t.brokerage_cents + t.fees_cents, costUnknown: !!t.cost_unknown });
      open.set(key, list);
      continue;
    }
    const proceeds = Math.round(t.units * t.price * 100) - t.brokerage_cents - t.fees_cents;
    let left = t.units;
    while (left > 1e-9 && list.length) {
      const p = list[0]; const take = Math.min(left, p.units);
      const cost = Math.round(p.costCents * (take / p.units));
      const share = Math.round(proceeds * (take / t.units));
      disposals.push({ sellId: t.id, owner: t.owner_entity_id, security: t.security_id, date: t.trade_date, fy: fyOf(parseISO(t.trade_date)), boughtOn: p.date,
        units: take, proceedsCents: share, costCents: cost, gainCents: p.costUnknown ? 0 : share - cost, discountable: parseISO(t.trade_date) > addYears(parseISO(p.date), 1), costUnknown: p.costUnknown });
      p.costCents -= cost; p.units -= take; left -= take;
      if (p.units <= 1e-9) list.shift();
    }
    if (left > 1e-9) problems.push({ security: t.security_id, owner: t.owner_entity_id, date: t.trade_date, units: left });
    open.set(key, list);
  }
  const holdings: Holding[] = [];
  for (const parcels of open.values()) {
    if (!parcels.length) continue;
    holdings.push({ owner: parcels[0].owner, security: parcels[0].security, account: parcels.at(-1)!.account, units: parcels.reduce((s, p) => s + p.units, 0),
      costCents: parcels.reduce((s, p) => s + p.costCents, 0), firstBought: parcels[0].date, parcels, unknownUnits: parcels.filter((p) => p.costUnknown).reduce((s, p) => s + p.units, 0) });
  }
  return { holdings, disposals, problems };
}

export type GainsYear = { fy: string; owner: string; gains: number; discountGains: number; losses: number; lossesBroughtIn: number; lossesUsed: number; net: number; lossesCarried: number; discountApplied: boolean };

/** Net capital gain per owner per financial year, carrying losses forward. */
export function gainsByYear(disposals: Disposal[], entities: Entity[]): GainsYear[] {
  const out: GainsYear[] = [];
  const owners = [...new Set(disposals.map((d) => d.owner))];
  for (const owner of owners) {
    const discountOk = entities.find((e) => e.id === owner)?.kind !== "company";
    const fys = [...new Set(disposals.filter((d) => d.owner === owner && !d.costUnknown).map((d) => d.fy))].sort();
    let carried = 0;
    for (const fy of fys) {
      const mine = disposals.filter((d) => d.owner === owner && d.fy === fy && !d.costUnknown);   // cost not recorded: left to the accountant
      const plain = mine.filter((d) => d.gainCents > 0 && !(d.discountable && discountOk)).reduce((s, d) => s + d.gainCents, 0);
      const disc = mine.filter((d) => d.gainCents > 0 && d.discountable && discountOk).reduce((s, d) => s + d.gainCents, 0);
      const losses = Math.abs(mine.filter((d) => d.gainCents < 0).reduce((s, d) => s + d.gainCents, 0));
      let pool = losses + carried;
      const usePlain = Math.min(pool, plain); pool -= usePlain;
      const useDisc = Math.min(pool, disc); pool -= useDisc;
      const net = (plain - usePlain) + Math.round((disc - useDisc) * (discountOk ? 0.5 : 1));
      out.push({ fy, owner, gains: plain + disc, discountGains: disc, losses, lossesBroughtIn: carried, lossesUsed: usePlain + useDisc, net, lossesCarried: pool, discountApplied: discountOk && disc - useDisc > 0 });
      carried = pool;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------
function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { for (const k of ["shares", "audit"]) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

/** Find or add a code (upper case), returning its id. */
export async function securityId(hid: string, code: string, name: string | null, existing: Security[]) {
  const c = code.trim().toUpperCase();
  const have = existing.find((s) => s.exchange === "ASX" && s.code === c);
  if (have) return have.id;
  const { data, error } = await supabase.from("securities").insert({ household_id: hid, exchange: "ASX", code: c, name: name || null }).select("id").single();
  if (error) throw error.code === "23514" ? new Error("Codes are letters and numbers, like VDHG.") : error;
  return (data as { id: string }).id;
}

export type TradeDraft = Omit<Trade, "id" | "security_id"> & { id?: string; code: string; name: string };
export function useSaveTrade() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async (a: { t: TradeDraft; securities: Security[] }) => {
      const { id, code, name, ...rest } = a.t;
      const security_id = await securityId(hid!, code, name, a.securities);
      const row = { ...rest, security_id };
      const { error } = id ? await supabase.from("share_trades").update(row).eq("id", id) : await supabase.from("share_trades").insert({ ...row, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useRemoveTrade() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("share_trades").update({ deleted_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export type IncomeDraft = Omit<Income, "id" | "security_id"> & { id?: string; code: string };
export function useSaveIncome() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async (a: { i: IncomeDraft; securities: Security[] }) => {
      const { id, code, ...rest } = a.i;
      const security_id = await securityId(hid!, code, null, a.securities);
      const row = { ...rest, security_id };
      const { error } = id ? await supabase.from("share_income").update(row).eq("id", id) : await supabase.from("share_income").insert({ ...row, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useRemoveIncome() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("share_income").update({ deleted_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** Type a price in by hand (e.g. from the broker's app). */
export function useSetPrice() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (a: { id: string; price: number; on: string }) => {
      const { error } = await supabase.from("securities").update({ last_price: a.price, last_price_on: a.on, price_source_url: null, price_checked_at: new Date().toISOString() }).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export type SyncResult = { id: string; code: string; outcome: PriceCheck["outcome"]; price?: number; as_at?: string; source_url?: string; message?: string };
/** The Sync button: the Worker looks each code up and posts the price back. */
export function useSyncPrices() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const r = await worker("/prices/sync", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids }) });
      const j = (await r.json().catch(() => ({}))) as { results?: SyncResult[]; message?: string };
      if (!r.ok || !j.results) throw new Error(j.message ?? "The price lookup didn't work.");
      return j.results;
    },
    onSettled: inv,
  });
}
